import { EFFECTS_PER_BLOCK_LIMIT } from '../constants';
import type { ConditionalEffect } from '../types/conditional-effect';
import type { ConditionalEffectPatch } from '../types/conditional-effect-patch';
import type { CreateTemplateConditionalEffectPayload } from '../types/create-template-conditional-effect-payload';
import type { EffectInput, EffectPatch } from '../types/effect-input';
import { isConditionalEffectPatch } from './type.helper';

const getContentIdsFromEffectInputs = (effects: EffectInput[] | EffectPatch[]): string[] =>
  effects
    .map(
      effect =>
        effect.editorNodeUpdateEffect?.contentId ||
        effect.editorRowUpdateEffect?.contentId ||
        effect.editorSectionUpdateEffect?.contentId
    )
    .filter((id): id is string => id !== undefined);

export const contentIdsWithLimitReached = (
  conditionalEffects: ConditionalEffect[],
  updatedConditionalEffect?:
    | ConditionalEffectPatch
    | CreateTemplateConditionalEffectPayload
    | undefined
): string[] => {
  const isUpdatingConditionalEffect =
    updatedConditionalEffect && isConditionalEffectPatch(updatedConditionalEffect);

  const filteredConditionalEffect = isUpdatingConditionalEffect
    ? conditionalEffects.filter(effect => effect.id !== updatedConditionalEffect.id)
    : conditionalEffects;

  const allContentIds = filteredConditionalEffect.flatMap(effect =>
    effect.truthyEffects
      .filter(truthyEffect => 'contentId' in truthyEffect)
      .map(truthyEffect => truthyEffect.contentId)
  );

  if (updatedConditionalEffect) {
    const updatedContentId = getContentIdsFromEffectInputs(updatedConditionalEffect.truthyEffects);
    allContentIds.push(...updatedContentId);
  }

  const contentIdCounts = allContentIds.reduce((counts, id) => {
    counts.set(id, (counts.get(id) || 0) + 1);
    return counts;
  }, new Map<string, number>());

  return Array.from(contentIdCounts.entries())
    .filter(([, count]) => count >= EFFECTS_PER_BLOCK_LIMIT)
    .map(([id]) => id);
};
